/**
 * useBraStatus — is the client's Buyer Representation Agreement signed?
 *
 * The BRA auto-sends when a client's dossier is complete (trec-svc on
 * dossier.completed). NAR 2024 requires it to be SIGNED before the client can
 * request a showing, so this drives both the home-screen "sign now" card and
 * the disabled state of the Request-a-showing button.
 */

import { useQuery } from "@tanstack/react-query";

import { formService } from "@re-platform/api-client";

import { api } from "../../lib/api";

const BRA_FORM_TYPE = "buyer_representation_agreement";

export interface BraStatus {
  loading: boolean;
  /** A BRA has been sent to the client (signed or not). */
  hasBra: boolean;
  /** The BRA is signed — showings are unlocked. */
  signed: boolean;
  /** The BRA form id to open for signing (null when there is no BRA). */
  formId: string | null;
}

export function useBraStatus(): BraStatus {
  const q = useQuery({
    queryKey: ["bra-status"],
    queryFn: () => formService.list(api),
    // Poll while unsigned so the UI unlocks shortly after the client signs.
    refetchInterval: (query) => {
      const bra = (query.state.data ?? []).find(
        (f) => f.form_type === BRA_FORM_TYPE,
      );
      return bra && bra.status !== "SIGNED" ? 10_000 : false;
    },
  });
  const bra = (q.data ?? []).find((f) => f.form_type === BRA_FORM_TYPE);
  return {
    loading: q.isPending,
    hasBra: bra != null,
    signed: bra?.status === "SIGNED",
    formId: bra?.id ?? null,
  };
}

export interface PreparingStatus {
  loading: boolean;
  /** Documents that exist for this client but have not been sent to them yet. */
  count: number;
}

/**
 * Documents the AGENT is still finishing — nothing for the client to do yet.
 *
 * A seller's or landlord's whole packet is held until the agent completes the
 * listing agreement's ¶2A legal description (gap I25), so between finishing
 * onboarding and receiving the signing email the client is in a state where the
 * correct answer is "nothing, we're on it". Saying nothing at all reads as the
 * application having stalled — which is what it looked like at Checkpoint #21.
 *
 * Shares the query key with `useBraStatus`, so the two hooks are one request.
 */
export function usePreparingDocuments(): PreparingStatus {
  const q = useQuery({
    queryKey: ["bra-status"],
    queryFn: () => formService.list(api),
    // Poll so the banner clears on its own once the agent sends.
    refetchInterval: (query) =>
      (query.state.data ?? []).some((f) => !f.docusign_envelope_id)
        ? 30_000
        : false,
  });
  // No envelope means it has never been sent. Status alone is not enough: a
  // WITHDRAWN form returns to an editable status but keeps its envelope id.
  const preparing = (q.data ?? []).filter(
    (f) =>
      !f.docusign_envelope_id &&
      (f.status === "PREFILLED" || f.status === "DRAFT"),
  );
  return { loading: q.isPending, count: preparing.length };
}
